<template>
  <ElDialog
    v-model="visible"
    append-to-body
    class="open-pc-dialog"
    width="640px"
    @close="handleClose"
  >
    <div class="title">
      {{ isMobile ? t('dt9AhdFOjWm6Q7G1dYPvM') : t('dt9AhdFOjWm6_7G1dYPvM') }}
    </div>
    <div class="subtitle">
      {{ isMobile ? t('sB1U6rpwfUAVfQc8Wv6J') : t('sB1U6rowfUAVfQc8Wv6J') }}
    </div>
    <div class="introduce-block">
      <div class="introduce-item">
        <i class="introduce-item__icon icon-speed" />
        <span class="introduce-item__text">{{ t('5rH9n8QZzeJv67Pknl2a') }}</span>
      </div>
      <div class="introduce-item">
        <i class="introduce-item__icon icon-subtitle" />
        <span class="introduce-item__text">{{ t('l69ev4xKcWrDznhNHe3Vg') }}</span>
      </div>
      <div class="introduce-item">
        <i class="introduce-item__icon icon-tracker" />
        <span class="introduce-item__text">{{ t('81t7zYpa48Qzc6eWjNrvu') }}</span>
      </div>
    </div>
    <div class="controller-bottom">
      <div
        class="download-describe"
        @click="handleChannelDownload"
        v-html="isMobile ? t('vrfNkAm10UbgCBqm4W13A') : t('vrfNkAmh0UbgCBqm4W13A')"
      />
      <div class="button-box">
        <div
          class="cancel"
          @click="handleCancel"
        >
          {{ t('cancel') }}
        </div>
        <PPButton
          :loading="isChecking"
          class="submit-button"
          type="primary"
          @click="handleOpenApp"
        >
          {{ isChecking ? t('3o8Psj2MNja2L0akcuz2H') : t('bCHtJ97hh2vwe3hs6vwLb') }}
        </PPButton>
      </div>
    </div>
  </ElDialog>
</template>

<script
  lang="ts"
  setup
>
import {
  computed,
  isIOS,
  isWindows,
  ref,
  toRefs,
  useI18n,
} from '#imports'
import PPButton from '@/components/PPButton.vue'
import { appInstallLink } from '@/composables/useInstallChannels'
import { useLayout } from '@/store/layout'
import { useUserInfo } from '@/store/userInfo'
import {
  generateAuthorizeCode,
  windowsClientId,
  windowsOrigin,
} from '@/utils'
import { APP_DEEPLINK_PREFIX } from '~/types/constants'
import toApp from '@/utils/toApp'
import { useShare } from '~/store/share'
import { AppInstallPlatform } from '~/types/enum'

const visible = ref(false)
const isChecking = ref(false)

const { t } = useI18n()
const layoutStore = useLayout()
const { isMobile } = toRefs(layoutStore)
const shareStore = useShare()
const userInfo = useUserInfo()

const downloadLink = computed(() => {
  const iosLink = appInstallLink(AppInstallPlatform.APPLE_STORE)
  const andLink = appInstallLink(AppInstallPlatform.ANDROID_APK)

  if (isMobile.value) {
    return isIOS() ? iosLink : andLink
  }

  if (isWindows()) {
    return 'https://download.mypikpak.com/desktop/official_PikPak.exe'
  }

  return ''
})

const hide = () => {
  visible.value = false
}

const handleCancel = () => {
  hide()
}

const handleClose = () => {
  hide()
}

const openInApp = () => {
  window.location.href = toApp.openFile(
    shareStore.restoreCache?.parentId || '',
    shareStore.restoreCache?.fileIds?.[0] || '',
  )
}

const openInWindows = async () => {
  try {
    const parentId = shareStore.restoreCache.parentId
    const userId = userInfo.userId || ''
    const fileIds = shareStore.restoreCache.fileIds?.[0] || ''
    const windowsAuthCode = await generateAuthorizeCode({
      clientId: windowsClientId,
      redirectUri: windowsOrigin,
    }) as string

    const params = new URLSearchParams()
    params.set('code', windowsAuthCode)
    params.set('parent_id', parentId ?? '') // 避免设置字符串 'undefined' 'null'
    params.set('user_id', userId)
    params.set('video_id', fileIds ?? '') // 避免设置字符串 'undefined' 'null'
    params.set('route_name', 'recent')
    const searchString = params.toString()
    window.location.href = `${APP_DEEPLINK_PREFIX}/drive/play_video?${searchString}`
  } catch (err) {
    console.error('open in windows error: ', err)
  }
}

const handleChannelDownload = () => {
  const a = document.createElement('a')
  a.download = 'PikPak.exe'
  a.target = '_blank'
  a.href = downloadLink.value

  a.click()
}

const checkStatus = () => {
  isChecking.value = true

  setTimeout(() => {
    isChecking.value = false

    // 跳转失败, 打开官网下载页
    // if (!document.hidden) {
    //   const downloadPage = `${location.origin}/${isMobile.value ? 'mobile-app' : 'desktop-app'}`
    //   window.open(downloadPage)
    // }
  }, 3000)
}

const handleOpenApp = () => {
  setTimeout(() => {
    if (isMobile.value) {
      openInApp()
    }

    if (isWindows()) {
      openInWindows()
    }
  }, 500)

  checkStatus()
}

const show = () => {
  visible.value = true
  // 展示时自动执行
  handleOpenApp()
}
defineExpose({ show, hide, visible })
</script>

<style lang="less">
.open-pc-dialog {
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  background: linear-gradient(347.81deg, #ecf7ff 24.57%, #dcefff 102.72%);

  .el-dialog__header {
    padding: 0;
  }
}

@media screen and (max-width: 768px) {
  .open-pc-dialog {
    width: calc(100vw - 32px) !important;

    .controller-bottom {
      flex-direction: column;

      .download-describe {
        align-self: flex-start;
      }

      .button-box {
        align-self: flex-end;
      }
    }
  }
}
</style>
<style
  lang="less"
  scoped
>
.open-pc-dialog {

  .title {
    color: #000;
    font-weight: 600;
    font-size: 18px;
  }

  .subtitle {
    margin-top: 12px;
    font-weight: 400;
    font-size: 14px;
    color: #666;
  }

  .introduce-block {
    display: flex;
    justify-content: space-evenly;
    align-items: flex-start;
    margin-top: 54px;

    .introduce-item {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      max-width: 160px;
      color: #000;
      margin: 0 6px;
      text-align: center;

      &__icon {
        width: 72px;
        height: 72px;
      }

      &__text {
        margin-top: 16px;
      }
    }
  }

  .controller-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 64px;
    gap: 24px;

    .download-describe {
      font-size: 14px;
      line-height: 1.5;
      color: #000;

      :deep(.download-button) {
        color: var(--color-primary);
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .button-box {
      display: flex;
      flex-shrink: 0;
      align-items: center;

      .cancel {
        cursor: pointer;
      }

      .submit-button {
        flex-shrink: 0;
        min-width: 120px;
        min-height: 36px;
        line-height: 1.4;
        padding: 7px 32px;
        background: #306eff;
        border-radius: 4px;
        margin-left: 24px;
        color: #fff;
        cursor: pointer;
      }
    }
  }
}

.icon-speed {
  background: url('@/assets/images/player/speed.png') no-repeat center/cover;
}

.icon-subtitle {
  background: url('@/assets/images/player/subtitle.png') no-repeat center/cover;
}

.icon-tracker {
  background: url('@/assets/images/player/tracker.png') no-repeat center/cover;
}
</style>
